import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { SeoMeta } from '../components/seo/SeoMeta';
import { generateChineseName } from '../lib/api/nameGenerator';
import { NameSuggestion } from '../types/name';
import { NameList } from '../components/name-generator/NameList';
import { ProgressBar } from '../components/ui/ProgressBar';

export function ChineseGirlNames() {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [surname, setSurname] = useState('');
  const [suggestions, setSuggestions] = useState<NameSuggestion[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName.trim() || !surname.trim()) {
      setError('Both first name and surname are required.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuggestions([]);

    try {
      const response = await generateChineseName(firstName.trim(), surname.trim(), 'girl');
      setSuggestions(response.suggestions);
      setSelectedIndex(0);
      
      // Scroll to results
      setTimeout(() => {
        const resultsElement = document.getElementById('name-results');
        if (resultsElement) {
          resultsElement.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error occurred while generating names');
    } finally {
      setIsLoading(false);
    }
  };

  // Popular girl names data
  const popularGirlNames = [
    { chinese: '雅婷', pinyin: 'Yǎtíng', meaning: 'Elegant and graceful', popularity: 5 },
    { chinese: '欣怡', pinyin: 'Xīnyí', meaning: 'Joyful and pleasant', popularity: 5 },
    { chinese: '梦琪', pinyin: 'Mèngqí', meaning: 'Dream jade', popularity: 4 },
    { chinese: '美琳', pinyin: 'Měilín', meaning: 'Beautiful jade', popularity: 4 },
    { chinese: '雅静', pinyin: 'Yǎjìng', meaning: 'Elegant and calm', popularity: 4 },
    { chinese: '婉婷', pinyin: 'Wǎntíng', meaning: 'Graceful and pretty', popularity: 4 },
  ];

  const cuteGirlNames = [
    { chinese: '小雨', pinyin: 'Xiǎoyǔ', meaning: 'Little rain' },
    { chinese: '糖糖', pinyin: 'Tángtáng', meaning: 'Sweet like sugar' },
    { chinese: '可儿', pinyin: 'Kě\'ér', meaning: 'Lovely child' },
    { chinese: '萌萌', pinyin: 'Méngméng', meaning: 'Adorable' },
    { chinese: '甜甜', pinyin: 'Tiántián', meaning: 'Sweet' },
    { chinese: '圆圆', pinyin: 'Yuányuán', meaning: 'Round/Complete' },
  ];

  const beautifulGirlNames = [
    { chinese: '美琳', pinyin: 'Měilín', meaning: 'Beautiful jade' },
    { chinese: '雅静', pinyin: 'Yǎjìng', meaning: 'Elegant and calm' },
    { chinese: '婉婷', pinyin: 'Wǎntíng', meaning: 'Graceful and pretty' },
    { chinese: '梅花', pinyin: 'Méihuā', meaning: 'Plum blossom' },
    { chinese: '莲花', pinyin: 'Liánhuā', meaning: 'Lotus flower' },
    { chinese: '樱桃', pinyin: 'Yīngtáo', meaning: 'Cherry' },
  ];

  return (
    <>
      <SeoMeta
        title="Chinese Girl Names - 200+ Beautiful Ideas | Chinese-Name.net"
        description="Chinese girl names with meanings, pinyin, and style guidance. Explore elegant, modern, and traditional options, then choose a beautiful name today."
        pathname="/chinese-girl-names"
      />

      {/* Breadcrumb */}
      <div className="bg-gray-50 py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="text-sm">
            <Link to="/" className="text-gray-500 hover:text-gray-700">Home</Link>
            <span className="mx-2 text-gray-400">/</span>
            <span className="text-gray-900">Chinese Girl Names</span>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-pink-50 to-rose-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl text-center mb-6">
            Chinese Girl Names: 200+ Beautiful Names with Meanings
          </h1>
          <p className="text-xl text-gray-600 text-center max-w-3xl mx-auto mb-8">
            Find the perfect Chinese name for your baby girl. This comprehensive guide features over 200 beautiful Chinese girl names, from cute and elegant to traditional classics. Each name includes Chinese characters, pinyin, and cultural meaning.
          </p>

          {/* Quick CTA */}
          <div className="bg-white rounded-lg shadow-md p-6 max-w-2xl mx-auto mb-8">
            <p className="text-center text-gray-700 mb-4">
              💡 Want a personalized girl name?
            </p>
            <form onSubmit={handleGenerate} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First Name"
                  className="px-4 py-2 border border-gray-300 rounded-md focus:ring-pink-500 focus:border-pink-500"
                  required
                />
                <input
                  type="text"
                  value={surname}
                  onChange={(e) => setSurname(e.target.value)}
                  placeholder="Last Name"
                  className="px-4 py-2 border border-gray-300 rounded-md focus:ring-pink-500 focus:border-pink-500"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-pink-600 text-white px-6 py-3 rounded-md hover:bg-pink-700 disabled:bg-gray-400 transition-colors font-medium"
              >
                {isLoading ? 'Generating...' : 'Generate Chinese Girl Name →'}
              </button>
            </form>
            <ProgressBar isGenerating={isLoading} duration={25000} />
            {error && <p className="mt-2 text-red-600 text-sm text-center">{error}</p>}
          </div>
        </div>
      </div>

      {/* Introduce Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              What Are Chinese Girl Names?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Chinese girl names are beautifully crafted to reflect grace, beauty, wisdom, and positive qualities. Each name combines Chinese characters (汉字) with specific meanings, creating names that convey elegance, virtue, and cultural significance. Whether traditional or modern, Chinese girl names carry deep meaning and are cherished in Chinese culture.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Why Choose a Chinese Girl Name?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
              Choosing a Chinese name for your daughter connects her to rich cultural traditions and meaningful symbolism.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <div className="text-4xl mb-3">🌸</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Beauty & Grace</h3>
              <p className="text-gray-600 text-sm">Chinese girl names often reflect beauty, elegance, and feminine virtues.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <div className="text-4xl mb-3">📚</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Cultural Heritage</h3>
              <p className="text-gray-600 text-sm">Connect your daughter to thousands of years of Chinese culture and tradition.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <div className="text-4xl mb-3">✨</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Meaningful Identity</h3>
              <p className="text-gray-600 text-sm">Each name carries deep meaning and positive aspirations for your child's future.</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Female Chinese names Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Female Chinese names
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Explore female Chinese names that highlight elegance, grace, and gentle strength—perfect for Chinese girls or women seeking names with soft yet powerful energy.
            </p>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            <div className="bg-pink-50 rounded-xl p-6 shadow-sm">
              <h3 className="text-2xl font-semibold text-gray-900 mb-3">Chinese names list female</h3>
              <p className="text-gray-600 mb-4">
                Browse curated lists of Chinese names list female that blend tradition with modern flair. Each entry shares the pronunciation, meaning, and tone that makes the name special.
              </p>
              <p className="text-sm text-gray-500">
                Keywords: Chinese names list female, female Chinese names
              </p>
            </div>
            <div className="bg-pink-50 rounded-xl p-6 shadow-sm">
              <h3 className="text-2xl font-semibold text-gray-900 mb-3">Chinese names girl</h3>
              <p className="text-gray-600 mb-4">
                Pick Chinese names girl suggestions with graceful meanings. We highlight Chinese girls names that sound beautiful, are easy to write, and feel right for your daughter or future self.
              </p>
              <p className="text-sm text-gray-500">
                Keywords: Chinese girls names, girl Chinese names
              </p>
            </div>
            <div className="bg-pink-50 rounded-xl p-6 shadow-sm">
              <h3 className="text-2xl font-semibold text-gray-900 mb-3">Chinese name translation</h3>
              <p className="text-gray-600 mb-4">
                Compare translations by seeing my name in Chinese, learning how the characters match the English meaning, and discovering how Chinese women names shift between cultures.
              </p>
              <p className="text-sm text-gray-500">
                Keywords: Chinese name translation, my name in Chinese, Chinese woman names, Chinese women names
              </p>
            </div>
          </div>
          <p className="text-gray-600 mt-8 text-center max-w-3xl mx-auto">
            These female Chinese names encourage you to connect with beauty, virtue, and cultural stories. We also record the Chinese name meaning so you can pronounce and spell the names with confidence.
          </p>
        </div>
      </section>

      {/* Table of Contents */}
      <div className="bg-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Quick Navigation</h2>
          <ul className="space-y-2 text-gray-600">
            <li>• <a href="#beautiful" className="text-pink-600 hover:underline">Beautiful Chinese Girl Names</a></li>
            <li>• <a href="#meanings" className="text-pink-600 hover:underline">Chinese Girl Names with Meaning</a></li>
            <li>• <a href="#modern" className="text-pink-600 hover:underline">Modern Chinese Girl Names</a></li>
            <li>• <a href="#traditional" className="text-pink-600 hover:underline">Traditional Chinese Girl Names</a></li>
            <li>• <a href="#generate" className="text-pink-600 hover:underline">Generate a Chinese Girl Name</a></li>
          </ul>
        </div>
      </div>

      {/* Generated Names Results */}
      {suggestions.length > 0 && (
        <div id="name-results" className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Your Generated Chinese Girl Names
            </h2>
            <NameList
              suggestions={suggestions}
              selectedIndex={selectedIndex}
              onSelect={setSelectedIndex}
            />
          </div>
        </div>
      )}

      {/* Most Popular Names */}
      <div id="popular" className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" id="beautiful">Beautiful Chinese Girl Names</h2>
            <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
              These are the most popular Chinese girl names in 2025, chosen by parents across China and Chinese communities worldwide. Each name carries positive meanings and embodies beauty and grace.
            </p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Chinese</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pinyin</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Meaning</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Popularity</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {popularGirlNames.map((name, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-lg font-semibold text-gray-900">{name.chinese}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-600">{name.pinyin}</td>
                    <td className="px-6 py-4 text-gray-600">{name.meaning}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {'⭐'.repeat(name.popularity)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Strong CTA Module */}
      <div className="bg-pink-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">🎯 Generate Your Perfect Chinese Girl Name</h3>
            <p className="text-lg text-gray-600 mb-6">Get personalized, beautiful Chinese girl names instantly</p>
            <Link
              to="/chinese-name-generator"
              className="inline-block bg-pink-600 text-white px-8 py-3 rounded-md hover:bg-pink-700 transition-colors font-medium text-lg"
            >
              Generate Chinese Girl Name Now →
            </Link>
          </div>
        </div>
      </div>

      {/* Cute Names */}
      <div id="modern" className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Modern Chinese Girl Names</h2>
          <p className="text-lg text-gray-600 mb-8">
            Modern Chinese girl names blend traditional elements with contemporary aesthetics. These names are popular among young Chinese parents today and often feature unique character combinations.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cuteGirlNames.map((name, index) => (
              <div key={index} className="bg-pink-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-gray-900 mb-1">{name.chinese}</div>
                <div className="text-pink-600 font-medium mb-2">{name.pinyin}</div>
                <div className="text-gray-600">{name.meaning}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Beautiful Names */}
      <section id="meanings" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Chinese Girl Names with Meaning</h2>
            <p className="text-lg text-gray-600 mb-6 max-w-3xl mx-auto">
              Every Chinese girl name carries profound meanings. Explore names with meanings that reflect beauty, grace, virtue, and positive aspirations.
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            <h3 className="text-2xl font-semibold text-gray-800 mb-4 text-center">
              Symbolism in Chinese Female Names
            </h3>
            <p className="text-gray-600 mb-8 max-w-3xl mx-auto text-center">
              Chinese female names often incorporate symbols of flowers (花), virtues (美德), and gentle imagery (柔和意象). These names reflect parents' hopes for their daughters to embody beauty, grace, and wisdom.
            </p>
          </div>
        </div>
      </section>

      <section id="beautiful" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Beautiful & Elegant Chinese Girl Names</h2>
            <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
              These sophisticated Chinese girl names embody beauty, grace, and elegance. Perfect for parents seeking refined, timeless names.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
            {beautifulGirlNames.map((name, index) => (
              <div key={index} className="bg-white p-4 rounded-lg shadow-sm">
                <div className="text-2xl font-bold text-gray-900 mb-1">{name.chinese}</div>
                <div className="text-pink-600 font-medium mb-2">{name.pinyin}</div>
                <div className="text-gray-600">{name.meaning}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Traditional Names */}
      <section id="traditional" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Traditional Chinese Girl Names</h2>
            <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
              Traditional Chinese girl names emphasize virtues like elegance, wisdom, kindness, and grace. These timeless names have been cherished for generations.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-gray-900 mb-1">文雅</div>
              <div className="text-pink-600 font-medium mb-2">Wényǎ</div>
              <div className="text-gray-600">Cultured and elegant</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-gray-900 mb-1">慧敏</div>
              <div className="text-pink-600 font-medium mb-2">Huìmǐn</div>
              <div className="text-gray-600">Wise and quick</div>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-gray-900 mb-1">淑贞</div>
              <div className="text-pink-600 font-medium mb-2">Shūzhēn</div>
              <div className="text-gray-600">Virtuous and pure</div>
            </div>
          </div>
        </div>
      </section>

      {/* Generate a Chinese Girl Name */}
      <section id="generate" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Generate a Chinese Girl Name</h2>
            <p className="text-lg text-gray-600 mb-6 max-w-3xl mx-auto">
              Use our free Chinese baby girl name generator to create personalized names instantly. Enter your preferences and get authentic Chinese girl names with meanings.
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6 max-w-2xl mx-auto">
            <form onSubmit={handleGenerate} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First Name"
                  className="px-4 py-2 border border-gray-300 rounded-md focus:ring-pink-500 focus:border-pink-500"
                  required
                />
                <input
                  type="text"
                  value={surname}
                  onChange={(e) => setSurname(e.target.value)}
                  placeholder="Last Name"
                  className="px-4 py-2 border border-gray-300 rounded-md focus:ring-pink-500 focus:border-pink-500"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-pink-600 text-white px-6 py-3 rounded-md hover:bg-pink-700 disabled:bg-gray-400 transition-colors font-medium"
              >
                {isLoading ? 'Generating...' : 'Generate Chinese Girl Name →'}
              </button>
            </form>
            <ProgressBar isGenerating={isLoading} duration={25000} />
            {error && <p className="mt-2 text-red-600 text-sm text-center">{error}</p>}
          </div>
        </div>
      </section>

      {/* How to Choose */}
      <section id="how-to-choose" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">How to Choose a Chinese Girl Name</h2>
          
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Step 1: Consider the Sound</h3>
              <p className="text-gray-600">
                Chinese girl names should have a soft, melodic sound. Test the name with your surname to ensure it flows beautifully.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Step 2: Focus on Meaning</h3>
              <p className="text-gray-600">
                Choose characters that convey positive qualities like beauty, grace, wisdom, or joy. Each character contributes to the overall meaning.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Step 3: Consider Cultural Appropriateness</h3>
              <p className="text-gray-600">
                Ensure the name is appropriate for different contexts - formal, casual, and professional settings.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Step 4: Test and Get Feedback</h3>
              <p className="text-gray-600">
                Say the name aloud, write it down, and get feedback from native Chinese speakers to ensure it sounds natural and beautiful.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Resources */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Related Resources</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">📚 Related Articles</h3>
              <ul className="space-y-2">
                <li><Link to="/cute-chinese-girl-names" className="text-pink-600 hover:underline">Cute Chinese Girl Names with Sweet Meanings</Link></li>
                <li><Link to="/blog/beautiful-chinese-girl-names-meanings" className="text-pink-600 hover:underline">Beautiful Chinese Girl Names Style Guide</Link></li>
                <li><Link to="/blog/how-to-choose-chinese-name" className="text-pink-600 hover:underline">How to Choose the Right Chinese Girl Name</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">👶 More Name Categories</h3>
              <ul className="space-y-2">
                <li><Link to="/chinese-boy-names" className="text-pink-600 hover:underline">Chinese Boy Names with Meanings</Link></li>
                <li><Link to="/chinese-last-names" className="text-pink-600 hover:underline">Chinese Last Names and Origins</Link></li>
                <li><Link to="/chinese-name-generator" className="text-pink-600 hover:underline">Generate a Personalized Chinese Girl Name</Link></li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions About Chinese Girl Names
            </h2>
          </div>
          
          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                What makes a good Chinese girl name?
              </h3>
              <p className="text-gray-600">
                A good Chinese girl name combines positive meanings, elegant pronunciation, and cultural significance. Names often reflect beauty, grace, wisdom, and feminine virtues.
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                How do I choose between traditional and modern Chinese girl names?
              </h3>
              <p className="text-gray-600">
                Traditional names carry deep cultural heritage and timeless meanings, while modern names blend traditional elements with contemporary aesthetics. Choose based on your family's values and preferences.
              </p>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                What are the most popular Chinese girl names?
              </h3>
              <p className="text-gray-600">
                Popular Chinese girl names include 雅婷 (Yǎtíng), 欣怡 (Xīnyí), and 梦琪 (Mèngqí). These names combine beautiful meanings with pleasant pronunciation.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Can I get beautiful Chinese names for girls with meaning?
              </h3>
              <p className="text-gray-600">
                Yes—every suggestion highlights beautiful Chinese names for girls with meaning, from floral imagery to virtues that honor your hopes.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                How does the female Chinese name generator work?
              </h3>
              <p className="text-gray-600">
                The female Chinese name generator mixes graceful characters, tone harmony, and modern taste so the names feel gentle yet confident.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Can I translate my name and say “my name is in Chinese”?
              </h3>
              <p className="text-gray-600">
                Yes. The Chinese name translation tools explain how to pronounce my name is in Chinese, list the Chinese name meaning, and help you share your Chinese names with friends.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-pink-600 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-3xl font-bold text-white mb-4">Find Your Perfect Chinese Girl Name</h3>
          <p className="text-xl text-pink-100 mb-8">Use our AI-powered generator to create personalized names instantly</p>
          <Link
            to="/chinese-name-generator"
            className="inline-block bg-white text-pink-600 px-8 py-3 rounded-md hover:bg-gray-100 transition-colors font-medium text-lg"
          >
            Generate Chinese Girl Name Now →
          </Link>
        </div>
      </section>
    </>
  );
}

